import {
    ConjunctionalAgencySchema,
    InternalConjunctionalAgencySchema,
} from '@buyerflo/core/schemas/conjunctionalAgency'
import { FrontendUserSchema } from '@buyerflo/core/schemas/userMembership'
import { zodResolver } from '@hookform/resolvers/zod'
import { DeleteRounded } from 'components/icons'
import Button from 'components/button'
import { Dialog, Separator, SizableText, Switch, View, XStack, YStack } from 'tamagui'
import { Label } from 'components/label'
import { Autocomplete } from 'components/autocomplete'
import { useMutation } from '@tanstack/react-query'
import { useConjunctionalAgenciesApi, useUserName, useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import Loading from 'components/Loading'
import { SlotAutocomplete } from 'components/autocomplete'
import { SlotInput, SlotWrapper } from 'components/input'
import Modal, { dontCloseOnBackgroundClick } from 'components/modal'
import SectionHead from 'components/sectionHead'
import { useShowSnack } from 'components/snackbar'
import { MobileFriendlyStack } from 'components/stack'
import { prop, sortBy, without } from 'ramda'
import { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
import { useTRPC } from 'utils/trpc'

interface XeroContact {
    id: string
    name: string
}

const XeroContactSearch = ({
    value,
    onChange,
}: {
    value: XeroContact | undefined
    onChange: (contact: XeroContact | null) => void
}) => {
    const [search, setSearch] = useState('')
    const [debouncedSearch] = useDebounce(search, 200)
    const [contacts, setContacts] = useState<{ id: string; name: string }[]>([])

    console.log('debouncedSearch', debouncedSearch)

    return (
        <Autocomplete
            value={value}
            onChange={(e, value) => onChange(value as XeroContact | null)}
            options={contacts}
            getOptionLabel={prop('name')}
            onInputChange={(e, value) => setSearch(value)}
        />
    )
}

const EditAgencyModal = ({
    agency,
    users,
    close,
}: {
    agency: {} | (ConjunctionalAgencySchema & { conjunctionalAgencyId: string })
    close
    users: FrontendUserSchema[]
}) => {
    const trpc = useTRPC()
    const conjunctionalAgenciesApi = useConjunctionalAgenciesApi()
    const usersApi = useUsersApi()
    const showSnack = useShowSnack()
    const userName = useUserName()
    const isNew = (agency as any).conjunctionalAgencyId == undefined
    const { user } = useUserData()
    const createAgencyMutation = useMutation(trpc.conjunctionalAgencies.createConjunctionalAgency.mutationOptions())
    const updateAgencyMutation = useMutation(trpc.conjunctionalAgencies.updateConjunctionalAgency.mutationOptions())
    const deleteAgencyMutation = useMutation(trpc.conjunctionalAgencies.deleteConjunctionalAgency.mutationOptions())

    const currentUsers = isNew
        ? []
        : users
              .filter(
                  user =>
                      user.conjunctionalAgencyId ==
                      (agency as InternalConjunctionalAgencySchema).conjunctionalAgencyId,
              )
              .map(prop('userId'))

    console.log('currentUsers', currentUsers)

    const { register, control, handleSubmit, formState, getValues, setValue } =
        useForm<ConjunctionalAgencySchema>({
            defaultValues: agency
                ? {
                      ...agency,
                      userIds: currentUsers,
                  }
                : {},
            resolver: zodResolver(ConjunctionalAgencySchema),
        })

    if (Object.keys(formState.errors).length > 0) {
        console.log(formState.errors)
    }

    const onSubmit: SubmitHandler<ConjunctionalAgencySchema> = async data => {
        try {
            if (isNew) {
                await createAgencyMutation.mutateAsync(data)
                close()
            } else {
                await updateAgencyMutation.mutateAsync({
                    conjunctionalAgencyId: (agency as any).conjunctionalAgencyId,
                    data: {
                        ...data,
                        removedUserIds: without(data.userIds, currentUsers),
                    },
                })
            }
            await Promise.all([conjunctionalAgenciesApi.refresh(), usersApi.refresh()])
            showSnack(`Conjunctional Agency ${isNew ? 'Added' : 'Saved'}`, 'success')
            close()
        } catch (e) {
            console.error(e)
            showSnack('Error saving conjunctional agency', 'danger')
        }
    }

    return (
        <form
            noValidate
            onSubmit={handleSubmit(onSubmit)}
        >
            <Dialog.Title fontWeight="700" letterSpacing={-0.3}>
                {isNew ? 'Create Conjunctional Agency' : 'Edit Conjunctional Agency'}
            </Dialog.Title>
            <YStack gap="$4">
                <Controller
                    name="licenseeName"
                    control={control}
                    render={field => (
                        <SlotInput
                            {...field}
                            label="Licensee Name"
                        />
                    )}
                />
                <MobileFriendlyStack>
                    <Controller
                        name="abn"
                        control={control}
                        render={field => (
                            <SlotInput
                                {...field}
                                label="ABN"
                            />
                        )}
                    />
                    <Controller
                        name="acn"
                        control={control}
                        render={field => (
                            <SlotInput
                                {...field}
                                label="ACN"
                            />
                        )}
                    />
                </MobileFriendlyStack>
                <MobileFriendlyStack>
                    <Controller
                        name="licenseNumber"
                        control={control}
                        render={field => (
                            <SlotInput
                                {...field}
                                label="Licensee Number"
                            />
                        )}
                    />
                    <Controller
                        name="licenseExpiry"
                        control={control}
                        render={field => (
                            <SlotInput
                                {...field}
                                type="date"
                                label="Licensee Expiry"
                            />
                        )}
                    />
                </MobileFriendlyStack>
                <Controller
                    name="agencyCommission"
                    control={control}
                    render={field => (
                        <SlotInput
                            {...field}
                            type="number"
                            endDecorator="%"
                            label="Agent Commission"
                            disabled={!user.permissions.includes('users.edit')}
                        />
                    )}
                />
                {user.permissions.includes('users.edit') && (
                    <Controller
                        name="agencyCommissionBeforeHeadOfficeCommission"
                        control={control}
                        disabled={!user.permissions.includes('users.edit')}
                        render={field => (
                            <YStack gap="$1">
                                <Label>Franchise Fee Applied To Agent Commission</Label>
                                <XStack alignItems="center" gap="$2" marginRight="auto">
                                    <SizableText opacity={!field.field.value ? 1 : 0.35}>
                                        After Franchise Fee
                                    </SizableText>
                                    <Switch
                                        size="$3"
                                        p={0}
                                        checked={field.field.value}
                                        onCheckedChange={checked => field.field.onChange(checked)}
                                    >
                                        <Switch.Thumb animation="quick" />
                                    </Switch>
                                    <SizableText opacity={field.field.value ? 1 : 0.35}>
                                        Of 100%
                                    </SizableText>
                                </XStack>
                            </YStack>
                        )}
                    />
                )}
                <Controller
                    name="bankDetails.accountName"
                    control={control}
                    render={field => (
                        <SlotInput
                            {...field}
                            label="Bank Details"
                            type="object"
                        />
                    )}
                />
                <MobileFriendlyStack>
                    <Controller
                        name="bankDetails.bsb"
                        control={control}
                        render={field => (
                            <SlotInput
                                {...field}
                                label="BSB"
                            />
                        )}
                    />
                    <Controller
                        name="bankDetails.accountNumber"
                        control={control}
                        render={field => (
                            <SlotInput
                                {...field}
                                label="Account Number"
                            />
                        )}
                    />
                </MobileFriendlyStack>

                <Controller
                    name="userIds"
                    control={control}
                    render={field => (
                        <SlotAutocomplete
                            {...field}
                            label="Users"
                            multiple
                            options={(usersApi.data ?? []).map(prop('userId'))}
                            getOptionLabel={userName}
                            onChange={(e, value) => field.field.onChange(value)}
                        />
                    )}
                />

                <Controller
                    name="xero"
                    control={control}
                    render={field => (
                        <SlotWrapper
                            {...field}
                            label="Xero Contact"
                        >
                            <XeroContactSearch {...field.field} />
                        </SlotWrapper>
                    )}
                />
            </YStack>
            <XStack gap="$2" justifyContent="flex-end" marginTop="$4">
                <Button
                    theme="primary"
                    onPress={handleSubmit(onSubmit)}
                    animation="quick"
                >
                    Save
                </Button>
                <Button
                    onPress={close}
                    variant="outlined"
                    animation="quick"
                >
                    Cancel
                </Button>
                {!isNew && (
                    <Button
                        theme="red"
                        variant="outlined"
                        icon={<DeleteRounded />}
                        marginRight="auto"
                        onPress={async () => {
                            await deleteAgencyMutation.mutateAsync({
                                conjunctionalAgencyId: (agency as any).conjunctionalAgencyId,
                            })
                            await Promise.all([conjunctionalAgenciesApi.refresh(), usersApi.refresh()])
                            showSnack('Conjunctional Agency Deleted', 'success')
                            close()
                        }}
                    >
                        Delete
                    </Button>
                )}
            </XStack>
        </form>
    )
}

const AgenciesList = () => {
    const conjunctionalAgenciesApi = useConjunctionalAgenciesApi()
    const usersApi = useUsersApi()
    const { user } = useUserData()

    const [editAgency, setEditAgency] = useState<
        {} | (ConjunctionalAgencySchema & { conjunctionalAgencyId?: string }) | undefined
    >()

    return (
        <>
            <SectionHead title="Conjunctional Agencies" />
            {conjunctionalAgenciesApi.data ? (
                <View maxWidth={640}>
                    {sortBy((a: InternalConjunctionalAgencySchema) => a.licenseeName.toLowerCase(), (conjunctionalAgenciesApi.data ?? []) as InternalConjunctionalAgencySchema[])
                        .map(agency => (
                            <YStack key={agency.conjunctionalAgencyId}>
                                <XStack
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    cursor="pointer"
                                    paddingHorizontal="$1"
                                    paddingTop="$1"
                                    animation="quick"
                                    hoverStyle={{
                                        backgroundColor: '$backgroundHover',
                                        borderRadius: '$2',
                                    }}
                                    pressStyle={{
                                        backgroundColor: '$backgroundPress',
                                        opacity: 0.85,
                                    }}
                                    onPress={() => setEditAgency(agency)}
                                >
                                    <XStack gap="$4" alignItems="flex-start" flex={1}>
                                        <SizableText fontWeight="600">
                                            {agency.licenseeName}
                                        </SizableText>
                                        <View flex={1} />
                                        <View>
                                            {usersApi.data?.filter(
                                                    user =>
                                                        user.conjunctionalAgencyId ==
                                                        agency.conjunctionalAgencyId,
                                                )
                                                .map(user => (
                                                    <View
                                                        key={user.userId}
                                                        backgroundColor="$primaryLight"
                                                        paddingHorizontal="$2"
                                                        paddingVertical="$0.5"
                                                        borderRadius={999}
                                                        marginHorizontal="$1"
                                                    >
                                                        <SizableText fontSize="$2">
                                                            {user.firstName} {user.lastName}
                                                        </SizableText>
                                                    </View>
                                                ))}
                                        </View>
                                    </XStack>
                                </XStack>
                                <Separator marginVertical="$1" />
                            </YStack>
                        ))}
                    {user.permissions.includes('users.edit') && (
                        <Button
                            marginTop="$4"
                            theme="primary"
                            onPress={() => setEditAgency({})}
                            animation="quick"
                        >
                            New Agency
                        </Button>
                    )}
                </View>
            ) : (
                <Loading />
            )}
            <Modal
                open={editAgency !== undefined}
                onOpenChange={(open) => { if (!open) setEditAgency(undefined) }}
            >
                {usersApi.data
                    ? editAgency && (
                              <EditAgencyModal
                                  agency={editAgency}
                                  users={usersApi.data!}
                                  close={() => setEditAgency(undefined)}
                              />
                          )
                    : (<Loading />)}
            </Modal>
        </>
    )
}

const ConjunctionalAgencies = () => {
    return (
        <View
            paddingHorizontal="$4"
            $sm={{ paddingHorizontal: '$6' }}
        >
            <AgenciesList />
        </View>
    )
}

export default ConjunctionalAgencies
